import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { call } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { MAX_RECORD, PaginationType } from 'constants/Global';
import type { PageContent } from 'modules/global/generic.types';

const PAGE_SIZE = 100; // Arbitrary large page size to minimize number of requests when fetching all pages

/**
 * Fetches all pages of a paginated endpoint by making sequential requests.
 *
 * Overrides `page` and `size` on each iteration — the caller's values are ignored.
 * Accumulates `content` across all pages and returns a single `PageContent<T>`.
 *
 * The returned `page` metadata is synthesized to reflect the aggregated result:
 * `number: 0`, `size/totalElements: allContent.length`, `totalPages: 1`.
 *
 * @template T The type of items in the paginated result
 * @param config Base Axios request config — `page` and `size` params are overridden internally
 */
export function* resolveAll<T>(config: AxiosRequestConfig): Generator<unknown, PageContent<T>, unknown> {
  const allContent: T[] = [];
  let pageNumber = 0;
  let totalPages = 0;
  let links: PageContent<T>['links'] = [];

  do {
    const pagedConfig: AxiosRequestConfig = {
      ...config,
      params: { ...config.params, page: pageNumber, size: PAGE_SIZE },
    };

    const { data } = (yield call(() => restClient.request<PageContent<T>>(pagedConfig))) as AxiosResponse<PageContent<T>>;

    allContent.push(...data.content);
    totalPages = data.page.totalPages;
    links = data.links;
    pageNumber++;
  } while (pageNumber < totalPages);

  return {
    content: allContent,
    page: {
      number: 0,
      size: allContent.length,
      totalElements: allContent.length,
      totalPages: 1,
      type: PaginationType.PAGE,
    },
    links,
  };
}

/**
 * Fetches a page (or all pages) from a paginated endpoint.
 *
 * When `config.params.size >= MAX_RECORD`, delegates to `resolveAll` which
 * fetches every page sequentially and returns the full result set.
 * Otherwise makes a single direct request.
 *
 * @template T The type of items in the paginated result
 * @param config Axios request config — passed as-is for single requests, overridden by resolveAll for paginated ones
 */
export function* fetchPage<T>(config: AxiosRequestConfig): Generator<unknown, PageContent<T>, unknown> {
  if ((config.params?.size ?? 0) >= MAX_RECORD) {
    return yield* resolveAll<T>(config);
  }
  const { data } = (yield call(() => restClient.request<PageContent<T>>(config))) as AxiosResponse<PageContent<T>>;
  return data;
}
